"use client";

import React, { memo, useState } from "react";
import styles from "./BrowseCard.module.scss";
import Text from "@components/atoms/Text";
import Image from "next/image";
import assetLoader from "@utils/internalImageLoder";
import { BrowseCardProps, BrowseCardOption } from "./BrowseCard.types";
import { useIsMobile } from "@hooks/use-mobile";
import Button from "@components/atoms/Button";
import { useBrowseCardNavigation } from "@hooks/useBrowseCardNavigation";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";
import { useCity } from "@context/CityContext";
import { getCurrentPageType } from "@utils/utils";

const RENDER_CONFIG = {
  brand: {
    desktop: {
      gridClassName: 'makeGridDesktop',
      containerClassName: 'makeDesktopcontainer',
      showViewAll: true,
      viewAllLimit: 12
    },
    mobile: {
      gridClassName: 'makeGrid',
      containerClassName: 'container',
      showViewAll: false,
      viewAllLimit: 0
    }
  },
  bodyType: {
    gridClassName: 'restContainer',
    containerClassName: 'container',
    itemClassName: 'restContainerItem',
    labelClassName: 'restContainerLabel',
    showArrow: true
  },
  fuelType: {
    gridClassName: 'restContainer',
    containerClassName: 'container',
    itemClassName: 'restContainerItem',
    labelClassName: 'restContainerLabel',
    showArrow: true
  },
  transmission: {
    gridClassName: 'restContainer',
    containerClassName: 'container',
    itemClassName: 'restContainerItem',
    labelClassName: 'restContainerLabel',
    showArrow: true
  },
  budget: {
    gridClassName: 'priceRangeGrid',
    containerClassName: 'container',
    itemClassName: 'priceRangeItem',
    labelClassName: 'priceRangeLabel'
  },
  seatingCapacity: {
    gridClassName: 'seatingCapacityGrid',
    containerClassName: 'container',
    itemClassName: 'seatingCapacityItem',
    labelClassName: 'seatingCapacityLabel',
    processLabel: true
  },
  feature: {
    gridClassName: 'restContainer',
    containerClassName: 'container',
    itemClassName: 'restContainerItem',
    labelClassName: 'restContainerLabel'
  },
  default: {
    gridClassName: 'restContainer',
    containerClassName: 'container',
    itemClassName: 'restContainerItem',
    labelClassName: 'restContainerLabel'
  }
} as const;

const BrowseCard = memo(({ type, title, parentTitle, options, onSlugChange }: BrowseCardProps) => {
  const isMobile = useIsMobile();
  const [showAllMakes, setShowAllMakes] = useState(false);
  const { handleBrowseCardClick, generateNewSlug } = useBrowseCardNavigation({ onSlugChange });
  const pageName = getCurrentPageType();
  const { city } = useCity();

  const handleOptionClick = async (optionValue: any) => {
    if (optionValue === '') return;

    handleBrowseCardClick(type, optionValue);
    window.scrollTo({ top: 0, behavior: 'smooth' });

    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: pageName,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_collection",
        selection: optionValue,
        filter_type: type,
        collection_name: parentTitle,
        city_name: city.name,
      },
    });
  };

  const getRenderConfig = () => {
    if (type === 'brand') {
      return isMobile ? RENDER_CONFIG.brand.mobile : RENDER_CONFIG.brand.desktop;
    }
    return RENDER_CONFIG[type as keyof typeof RENDER_CONFIG] || RENDER_CONFIG.default;
  };

  const config = getRenderConfig();

  const getDisplayedOptions = () => {
    if (type === 'brand' && !isMobile && 'showViewAll' in config && config.showViewAll && !showAllMakes) {
      return options.slice(0, 'viewAllLimit' in config ? config.viewAllLimit : 12);
    }
    return options;
  };

  const displayedOptions = getDisplayedOptions();

  const renderOption = (option: BrowseCardOption, index: number): React.ReactElement => {
    const itemClassName = 'itemClassName' in config ? config.itemClassName : 'makeGridItem';
    const labelClassName = 'labelClassName' in config ? config.labelClassName : 'makeOptionLabel';
    const getProcessedLabel = (): React.ReactNode => {
      if ('processLabel' in config && config.processLabel && type === 'seatingCapacity') {
        const labelContainsSeater = option.label.toLowerCase().includes('seater');
        if (labelContainsSeater) {
          return option.label;
        }
        return (
          <>
            <Text className={styles.seatingCapacityLabel} ><span className={styles.seatingCapacityNumberLabel}>{option.label}</span> Seater</Text>
          </>
        );
      }
      return option.label;
    };

    const shouldShowIcon = option.icon && (type === 'brand');
    const shouldShowArrow = 'showArrow' in config && config.showArrow;

    const href = generateNewSlug(type, option.value);

    return (
      <a
        key={index}
        href={href ? `/${process.env.NEXT_PUBLIC_BASE_TERM ?? ""}collection/${href}` : '#'}
        className={styles[itemClassName]}
        onClick={(e) => {
          e.preventDefault();
          handleOptionClick(option.value);
        }}
        aria-label={`Browse ${option.label}`}
      >
        {shouldShowIcon && option.icon ? (
          <div className={styles.makeIconContainer}>
            <Image
              src={option.icon}
              alt={option.label}
              width={46}
              height={46}
              className={styles.makeIcon}
              loading='lazy'
              onError={(e) => {
                (e.target as HTMLImageElement).src = `${assetLoader()}/icons/fallbackCarLogo.svg`;
              }}
            />
          </div>
        ) : null}

        <Text className={styles[labelClassName]}>
          {getProcessedLabel()}
        </Text>

        {shouldShowArrow ? (
          <div className={styles.optionContent}>
            <Image
              src={`${assetLoader()}/icons/Arrow_right_dark.svg`}
              alt="Arrow_right_dark"
              width={24}
              height={24}
              loading='lazy'
            />
            {option.icon && (
              <Image
                src={option.icon}
                alt={option.label}
                className={styles.optionIcon}
                fill
                loading='lazy'
              />
            )}
          </div>
        ) : null}
      </a>
    );
  };

  const renderViewAllButton = () => {
    if (type !== 'brand' || !('showViewAll' in config) || !config.showViewAll) {
      return null;
    }

    const viewAllLimit = 'viewAllLimit' in config ? config.viewAllLimit : 12;

    if (options.length <= viewAllLimit) {
      return null;
    }

    return (
      <Button
        className={styles.viewAllButton}
        onClick={() => setShowAllMakes(!showAllMakes)}
      >
        <Text className={styles.viewAllText}>
          {showAllMakes ? 'View less brands' : 'View all brands'}
        </Text>
        <Image
          src={`${assetLoader()}/icons/Chevron_down.svg`}
          alt={showAllMakes ? 'View less brands' : 'View all brands'}
          width={20}
          height={20}
          className={`${styles.chevronIcon} ${showAllMakes ? styles.chevronUp : ''}`}
          loading='lazy'
        />
      </Button>
    );
  };

  const containerClassName = 'containerClassName' in config ? config.containerClassName : 'container';
  const gridClassName = 'gridClassName' in config ? config.gridClassName : 'makeGrid';

  return (
    <div className={styles.browseCard}>
      <div className={styles[containerClassName]}>
        <Text className={styles.title}>{title}</Text>

        <div className={styles[gridClassName]}>
          {displayedOptions?.map((option, index) => renderOption(option, index))}
        </div>

        {renderViewAllButton()}
      </div>
    </div>
  );
});

BrowseCard.displayName = "BrowseCard";

export default BrowseCard;
